import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  TrendingUp,
  TrendingDown,
  Zap,
  Building,
  Calendar,
  Share2,
  Copy,
  Check,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
  Upload,
  Edit,
  Sparkles,
  Pencil,
  RotateCcw,
  Save,
  X,
  FileDown,
  User,
  Image,
  Trash2
} from "lucide-react";
import Navigation from "@/components/Navigation";
import PublicNavigation from "@/components/PublicNavigation";
import EditConsumptionDataModal from "@/components/EditConsumptionDataModal";
import AiAnalysisDisplay from "@/components/AiAnalysisDisplay";
import { ContactModal } from "@/components/ContactModal";
import { generateMonositePDF } from "../utils/pdfExport/index";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const ConsumptionReportViewPage = () => {
  const { id: shareToken } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublicView, setIsPublicView] = useState(true);
  const [copied, setCopied] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAiAnalysis, setLoadingAiAnalysis] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Analysis editing states
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  
  // Title editing states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  
  // Contact info modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    title: '',
    agency: '',
    phone: '',
    email: '',
    address: ''
  });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    loadReport();
  }, [shareToken]);

  useEffect(() => {
    if (!authLoading) {
      setIsPublicView(!user);
    }
  }, [user, authLoading]);

  // Record view for non-authenticated users (clients)
  const recordView = async () => {
    if (!user) {
      try {
        await axios.post(`${API}/api/consumption-reports/${shareToken}/view`);
      } catch (err) {
        // Silently fail - view tracking should not affect user experience
        console.log('View not recorded');
      }
    }
  };

  // Record view after report is loaded (only for non-authenticated users)
  useEffect(() => {
    if (report && !user && !authLoading) {
      recordView();
    }
  }, [report, user, authLoading]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/consumption-reports/${shareToken}`);
      if (response.data.success) {
        const reportData = response.data.report;
        
        // Redirect to multisites page if this is a multisites report
        if (reportData.report_type === 'consumption_multisites') {
          navigate(`/consumption/multisites/${shareToken}`, { replace: true });
          return;
        }
        
        setReport(reportData);
        
        // Load existing analysis if available
        if (reportData.energy_manager_analysis) {
          setAiAnalysis(reportData.energy_manager_analysis);
        }
        
        // Load existing contact info if available
        if (reportData.contact_info) {
          setContactInfo(prev => ({
            ...prev,
            ...reportData.contact_info
          }));
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  // Save contact info for PDF
  const handleSaveContactInfo = async () => {
    try {
      setSavingContact(true);
      const response = await axios.put(
        `${API}/api/reports/${shareToken}/contact-info`,
        contactInfo,
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        setReport(prev => ({ ...prev, contact_info: response.data.contact_info }));
        setShowContactModal(false);
        toast.success('Coordonnées enregistrées', {
          description: 'Les coordonnées seront affichées sur la dernière page du PDF.'
        });
      }
    } catch (err) {
      console.error('Error saving contact info:', err);
      toast.error('Erreur lors de l\'enregistrement des coordonnées');
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      toast.error('Le titre ne peut pas être vide');
      return;
    }
    setSavingTitle(true);
    try {
      const response = await axios.patch(
        `${API}/api/consumption-reports/${shareToken}`,
        { building_name: editedTitle.trim() },
        { headers: getAuthHeader() }
      );
      if (response.data.success) {
        setReport(prev => ({ ...prev, building_name: editedTitle.trim() }));
        setIsEditingTitle(false);
        toast.success('Titre mis à jour avec succès');
      }
    } catch (err) {
      toast.error('Erreur lors de la mise à jour du titre');
    } finally {
      setSavingTitle(false);
    }
  };


  const handleShare = () => {
    const shareUrl = `${window.location.origin}/consumption/report/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Lien copié !', {
      description: 'Le lien de partage a été copié dans le presse-papiers.',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const generateAiAnalysis = async () => {
    if (!user) {
      toast.error('Vous devez être connecté pour utiliser cette fonctionnalité');
      return;
    }

    setLoadingAiAnalysis(true);
    setAiError(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/consumption-reports/${shareToken}/ai-analysis`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        setAiAnalysis(response.data.analysis);
        toast.success('Analyse Energy Manager générée avec succès !');
      }
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de la génération de l\'analyse';
      setAiError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingAiAnalysis(false);
    }
  };

  // Start editing the analysis
  const startEditingAnalysis = () => {
    setEditedAnalysis(aiAnalysis);
    setIsEditingAnalysis(true);
  };

  // Cancel editing
  const cancelEditingAnalysis = () => {
    setEditedAnalysis('');
    setIsEditingAnalysis(false);
  };

  // Save edited analysis
  const saveEditedAnalysis = async () => {
    if (!user || !editedAnalysis.trim()) return;
    
    setSavingAnalysis(true);
    try {
      const response = await axios.put(
        `${API}/api/consumption-reports/${report.share_token}/analysis`,
        { analysis: editedAnalysis },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setAiAnalysis(editedAnalysis);
        setIsEditingAnalysis(false);
        setEditedAnalysis('');
        toast.success('Analyse sauvegardée', {
          description: 'Vos modifications ont été enregistrées.'
        });
      }
    } catch (error) {
      console.error('Save analysis error:', error);
      toast.error('Erreur lors de la sauvegarde', {
        description: 'Veuillez réessayer.'
      });
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleExportPDF = async () => {
    if (!report) {
      toast.error('Données insuffisantes pour générer le PDF');
      return;
    }
    
    try {
      toast.info('Génération du PDF en cours...');
      // Pass the report with the current AI analysis state included
      const reportWithAnalysis = {
        ...report,
        energy_manager_analysis: aiAnalysis || report.energy_manager_analysis
      };
      const fileName = await generateMonositePDF(reportWithAnalysis);
      toast.success('PDF téléchargé !', {
        description: `Fichier: ${fileName}`
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  // Logo upload/delete functions
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté', {
        description: 'Veuillez utiliser une image JPG, PNG, GIF ou WebP.'
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop volumineux', {
        description: 'La taille maximale est de 2 Mo.'
      });
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/consumption-reports/${shareToken}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeader()
          }
        }
      );

      if (response.data.success) {
        // Update report with new logo
        setReport(prev => ({
          ...prev,
          client_logo: response.data.logo_url
        }));
        setLogoError(false);
        toast.success('Logo mis à jour !');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo client ?')) {
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await axios.delete(
        `${API}/api/consumption-reports/${shareToken}/logo`,
        {
          headers: getAuthHeader()
        }
      );

      if (response.data.success) {
        // Update report to remove logo
        setReport(prev => ({
          ...prev,
          client_logo: null
        }));
        toast.success('Logo supprimé');
      }
    } catch (error) {
      console.error('Logo delete error:', error);
      toast.error('Erreur lors de la suppression du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHourlyCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Format invalide', {
        description: 'Veuillez sélectionner un fichier CSV.',
      });
      return;
    }

    setUploadingCsv(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/consumption-reports/${shareToken}/hourly-data`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success('Données horaires importées', {
          description: `${response.data.data_points} points de données ajoutés.`,
        });
        // Reload report to get updated data
        loadReport();
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error('Erreur lors de l\'import', {
        description: error.response?.data?.detail || 'Une erreur est survenue.',
      });
    } finally {
      setUploadingCsv(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Prepare chart data with forecast/estimated consumption
  const getMonthlyChartData = () => {
    if (!report?.monthly_data?.current_year) return [];
    
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const data = [];
    
    // Determine the cutoff month for measured vs estimated data
    // Priority: 1) report_month_num, 2) report_month string, 3) last_month.name, 4) report_date, 5) current month
    let reportMonthNum = parseInt(report.report_month_num || '0');
    
    if (reportMonthNum === 0 && report.report_month) {
      const monthNamesLower = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                               'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      const monthStr = report.report_month.toLowerCase().split(' ')[0];
      reportMonthNum = monthNamesLower.findIndex(m => monthStr.startsWith(m.substring(0, 3))) + 1;
    }
    
    // Use last_month.name as the most reliable indicator of the last measured month
    if (reportMonthNum === 0 && report.last_month?.name) {
      const monthNamesLower = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                               'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      const lastMonthStr = report.last_month.name.toLowerCase();
      reportMonthNum = monthNamesLower.findIndex(m => lastMonthStr.startsWith(m.substring(0, 3))) + 1;
    }
    
    // Try parsing report_date (DD/MM/YYYY) - the month before the report date is the last measured
    if (reportMonthNum === 0 && report.report_date) {
      const parts = report.report_date.split('/');
      if (parts.length === 3) {
        const reportMonth = parseInt(parts[1]);
        // Report generated in month X means data is available up to month X-1
        reportMonthNum = reportMonth > 1 ? reportMonth - 1 : 12;
      }
    }
    
    // Fallback to current month
    if (reportMonthNum === 0) {
      reportMonthNum = new Date().getMonth() + 1;
    }
    
    Object.entries(report.monthly_data.current_year).forEach(([key, value]) => {
      const monthIndex = parseInt(key) - 1;
      const monthNum = parseInt(key);
      const hasConsumption = (value.consumption_mwh || 0) > 0;
      
      // Months up to reportMonthNum are MEASURED, after are ESTIMATED
      const isMeasuredMonth = monthNum <= reportMonthNum;
      
      // For measured months: show consumption as measured
      // For future months: show consumption (which is projection) as estimated
      const measuredValue = isMeasuredMonth && hasConsumption ? value.consumption_mwh : 0;
      const estimatedValue = !isMeasuredMonth && hasConsumption ? value.consumption_mwh : 0;
      
      data.push({
        month: months[monthIndex],
        // Measured consumption (blue bars) - only for months up to report month
        measured: measuredValue,
        // Estimated/Forecast consumption (yellow/pink bars) - for future months
        estimated: estimatedValue,
        // Previous year (gray bars)
        previousYear: value.previous_year_mwh || 0
      });
    });
    
    return data.sort((a, b) => months.indexOf(a.month) - months.indexOf(b.month));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
        {isPublicView ? <Navigation isSharedView={true} /> : <Navigation />}
        <div className="container mx-auto px-4 py-20 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement du rapport...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
        {isPublicView ? <Navigation isSharedView={true} /> : <Navigation />}
        <div className="container mx-auto px-4 py-20">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Rapport introuvable'}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const monthlyData = getMonthlyChartData();
  const diffPercent = report.last_month?.diff_percent || 0;
  const isImprovement = diffPercent < 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/10 to-background">
      {isPublicView ? <Navigation isSharedView={true} /> : <Navigation />}
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Client Logo Section - Editable for authenticated users */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {report.client_logo && !logoError ? (
              <img 
                src={report.client_logo.startsWith('data:') ? report.client_logo : `${API}${report.client_logo}`}
                alt="Logo client" 
                className="h-12 sm:h-16 w-auto max-w-[200px] object-contain"
                data-testid="client-logo"
                onError={() => setLogoError(true)}
              />
            ) : (
              !isPublicView && user && (
                <div className="text-sm text-muted-foreground italic">
                  Aucun logo client
                </div>
              )
            )}
          </div>
          
          {/* Logo management buttons for authenticated users */}
          {!isPublicView && user && (
            <div className="flex items-center gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Image className="w-4 h-4 mr-2" />
                )}
                {report.client_logo ? 'Modifier le logo' : 'Ajouter un logo'}
              </Button>
              {report.client_logo && !logoError && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogoDelete}
                  disabled={uploadingLogo}
                  className="text-destructive hover:text-destructive"
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              {isEditingTitle ? (
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-2xl font-bold h-12 max-w-lg"
                    data-testid="edit-title-input"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') setIsEditingTitle(false);
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleSaveTitle}
                    disabled={savingTitle}
                    data-testid="save-title-btn"
                  >
                    {savingTitle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-green-600" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setIsEditingTitle(false)}
                    data-testid="cancel-title-btn"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-3xl font-bold" data-testid="report-title">
                    Rapport Énergétique - {report.building_name || 'Consommation'}
                  </h1>
                  {!isPublicView && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditedTitle(report.building_name || '');
                        setIsEditingTitle(true);
                      }}
                      data-testid="edit-title-btn"
                      className="opacity-50 hover:opacity-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                {report.pdl && (
                  <span className="flex items-center gap-1">
                    Référence: <strong>{report.pdl}</strong>
                  </span>
                )}
                {report.segment && (
                  <Badge variant="secondary">Segment: {report.segment}</Badge>
                )}
                {report.period && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Période: {report.period}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {!isPublicView && (
                <Button onClick={handleShare} variant="outline">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4 mr-2" />
                      Partager
                    </>
                  )}
                </Button>
              )}
              {user && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowContactModal(true)}
                  data-testid="contact-pdf-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Contact PDF
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Exporter PDF
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {report.last_month && (
            <Alert className={`${isImprovement ? 'border-success bg-success/10' : 'border-warning bg-warning/10'}`}>
              <TrendingUp className="w-4 h-4" />
              <AlertDescription>
                {isImprovement ? 'Objectif mensuel atteint' : 'Augmentation de la consommation'} - 
                Consommation de {report.last_month.name} {isImprovement ? 'inférieure' : 'supérieure'} de{' '}
                <strong>{Math.abs(diffPercent)}%</strong> par rapport à l'année dernière
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Report Month Consumption - same as PDF */}
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>
                Consommation {report.report_month ? report.report_month.split(' ')[0] : (report.last_month?.name || '')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Calculate consumption for the report month (same logic as PDF)
                const monthlyData = report.monthly_data?.current_year || {};
                const prevYearData = report.monthly_data?.previous_year || {};
                const monthNamesLower = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                                         'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
                
                let reportMonthNum = parseInt(report.report_month_num || '0');
                
                if (reportMonthNum === 0 && report.report_month) {
                  const monthStr = report.report_month.toLowerCase().split(' ')[0];
                  reportMonthNum = monthNamesLower.findIndex(m => monthStr.startsWith(m.substring(0, 3))) + 1;
                }
                
                // Fallback to last_month if no report_month
                if (reportMonthNum === 0 && report.last_month?.name) {
                  const monthStr = report.last_month.name.toLowerCase();
                  reportMonthNum = monthNamesLower.findIndex(m => monthStr.startsWith(m.substring(0, 3))) + 1;
                }
                
                if (reportMonthNum === 0) {
                  reportMonthNum = new Date().getMonth() + 1;
                }
                
                const monthKey = reportMonthNum.toString().padStart(2, '0');
                const monthData = monthlyData[monthKey] || {};
                const consumption = monthData.consumption_mwh || monthData.measured_mwh || monthData.estimated_mwh || 0;
                const prevConsumption = prevYearData[monthKey]?.consumption_mwh || monthData.previous_year_mwh || 0;
                
                const diffPercent = prevConsumption > 0 
                  ? ((consumption - prevConsumption) / prevConsumption * 100).toFixed(1)
                  : 0;
                const isImprovement = parseFloat(diffPercent) < 0;
                
                return (
                  <>
                    <div className="text-3xl font-bold mb-2">
                      {consumption.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} <span className="text-lg text-muted-foreground">MWh</span>
                    </div>
                    {prevConsumption > 0 && (
                      <div className={`flex items-center gap-1 text-sm ${isImprovement ? 'text-success' : 'text-destructive'}`}>
                        {isImprovement ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                        <span>{diffPercent}% vs N-1</span>
                      </div>
                    )}
                    {prevConsumption > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        N-1 : {prevConsumption.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
                      </p>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Current Year YTD - Cumul à date */}
          {report.totals && (
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Cumul à date</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const monthlyData = report.monthly_data?.current_year || {};
                  const prevYearData = report.monthly_data?.previous_year || {};
                  
                  const monthNamesFr = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                  
                  // Determine the YTD cutoff month from report_date
                  let ytdMonth = 12;
                  if (report.report_date) {
                    const parts = report.report_date.split('/');
                    if (parts.length >= 2) {
                      ytdMonth = parseInt(parts[1]);
                    }
                  }
                  
                  // Determine the real last MEASURED month (same logic as chart)
                  let realMeasuredMonth = 0;
                  if (report.report_month_num) {
                    realMeasuredMonth = parseInt(report.report_month_num);
                  }
                  if (realMeasuredMonth === 0 && report.report_month) {
                    const monthNamesLower = ['janvier', 'f\u00e9vrier', 'mars', 'avril', 'mai', 'juin', 
                                             'juillet', 'ao\u00fbt', 'septembre', 'octobre', 'novembre', 'd\u00e9cembre'];
                    const monthStr = report.report_month.toLowerCase().split(' ')[0];
                    realMeasuredMonth = monthNamesLower.findIndex(m => monthStr.startsWith(m.substring(0, 3))) + 1;
                  }
                  if (realMeasuredMonth === 0 && report.last_month?.name) {
                    const monthNamesLower = ['janvier', 'f\u00e9vrier', 'mars', 'avril', 'mai', 'juin', 
                                             'juillet', 'ao\u00fbt', 'septembre', 'octobre', 'novembre', 'd\u00e9cembre'];
                    const lastMonthStr = report.last_month.name.toLowerCase();
                    realMeasuredMonth = monthNamesLower.findIndex(m => lastMonthStr.startsWith(m.substring(0, 3))) + 1;
                  }
                  if (realMeasuredMonth === 0 && report.report_date) {
                    const parts = report.report_date.split('/');
                    if (parts.length === 3) {
                      const rdMonth = parseInt(parts[1]);
                      realMeasuredMonth = rdMonth > 1 ? rdMonth - 1 : 12;
                    }
                  }
                  if (realMeasuredMonth === 0) realMeasuredMonth = ytdMonth;
                  
                  let ytdCurrent = 0;
                  let ytdPrevious = 0;
                  let lastMonthWithData = 0;
                  let measuredCount = 0;
                  let estimatedCount = 0;
                  
                  Object.entries(monthlyData).forEach(([month, data]) => {
                    const monthNum = parseInt(month);
                    const consumption = data.consumption_mwh || data.measured_mwh || 0;
                    
                    if (monthNum <= ytdMonth && consumption > 0) {
                      ytdCurrent += consumption;
                      ytdPrevious += (prevYearData[month]?.consumption_mwh || data.previous_year_mwh || 0);
                      if (monthNum > lastMonthWithData) {
                        lastMonthWithData = monthNum;
                      }
                      if (monthNum <= realMeasuredMonth) {
                        measuredCount++;
                      } else {
                        estimatedCount++;
                      }
                    }
                  });
                  
                  const ytdDiffPercent = ytdPrevious > 0 
                    ? ((ytdCurrent - ytdPrevious) / ytdPrevious * 100).toFixed(1)
                    : 0;
                  
                  const monthName = lastMonthWithData > 0 && lastMonthWithData <= 12 
                    ? monthNamesFr[lastMonthWithData - 1]
                    : '';
                  
                  let currentYear = new Date().getFullYear();
                  if (report.report_date) {
                    const yearMatch = report.report_date.match(/\d{4}/);
                    if (yearMatch) currentYear = parseInt(yearMatch[0]);
                  }
                  const prevYear = currentYear - 1;
                  
                  return (
                    <>
                      <div className="text-3xl font-bold mb-2">
                        {ytdCurrent.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} <span className="text-lg text-muted-foreground">MWh</span>
                      </div>
                      {ytdPrevious > 0 && (
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <span className={parseFloat(ytdDiffPercent) < 0 ? 'text-success' : 'text-destructive'}>
                            {parseFloat(ytdDiffPercent) > 0 ? '+' : ''}{ytdDiffPercent}% vs N-1
                          </span>
                        </div>
                      )}
                      {monthName && (
                        <p className="text-xs text-muted-foreground">
                          Fin {monthName} {currentYear}
                        </p>
                      )}
                      {estimatedCount > 0 && (
                        <p className="text-xs text-pink-500 mt-0.5">
                          dont {estimatedCount} mois estimé{estimatedCount > 1 ? 's' : ''}
                        </p>
                      )}
                      {ytdPrevious > 0 && monthName && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Fin {monthName} {prevYear} : {ytdPrevious.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh
                        </p>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Subscribed Powers - Modern Design */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-4 bg-gradient-to-r from-slate-50 to-transparent">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Puissances souscrites
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">Segment tarifaire</span>
                    <Badge variant="outline" className="font-mono">
                      {report.segment || 'Non renseigné'}
                    </Badge>
                  </div>
                </div>
                {!isPublicView && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEditModal(true)}
                    className="hover:bg-amber-50"
                  >
                    <Edit size={16} className="mr-2" />
                    Modifier
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {report.subscribed_powers && Object.values(report.subscribed_powers).some(v => v !== null && v !== undefined) ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {report.subscribed_powers.base && (
                    <div className="group relative bg-gradient-to-br from-slate-50 to-slate-100/50 hover:from-slate-100 hover:to-slate-200/50 rounded-xl p-4 border border-slate-200 transition-all duration-200">
                      <div className="text-xs font-medium text-slate-500 mb-1.5">Base</div>
                      <div className="text-2xl font-bold text-slate-900">{report.subscribed_powers.base}</div>
                      <div className="text-xs text-slate-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hc && (
                    <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100/50 hover:from-blue-100 hover:to-blue-200/50 rounded-xl p-4 border border-blue-200 transition-all duration-200">
                      <div className="text-xs font-medium text-blue-600 mb-1.5">HC</div>
                      <div className="text-2xl font-bold text-blue-900">{report.subscribed_powers.hc}</div>
                      <div className="text-xs text-blue-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hp && (
                    <div className="group relative bg-gradient-to-br from-orange-50 to-orange-100/50 hover:from-orange-100 hover:to-orange-200/50 rounded-xl p-4 border border-orange-200 transition-all duration-200">
                      <div className="text-xs font-medium text-orange-600 mb-1.5">HP</div>
                      <div className="text-2xl font-bold text-orange-900">{report.subscribed_powers.hp}</div>
                      <div className="text-xs text-orange-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hcb && (
                    <div className="group relative bg-gradient-to-br from-cyan-50 to-cyan-100/50 hover:from-cyan-100 hover:to-cyan-200/50 rounded-xl p-4 border border-cyan-200 transition-all duration-200">
                      <div className="text-xs font-medium text-cyan-600 mb-1.5">HCB</div>
                      <div className="text-2xl font-bold text-cyan-900">{report.subscribed_powers.hcb}</div>
                      <div className="text-xs text-cyan-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hch && (
                    <div className="group relative bg-gradient-to-br from-indigo-50 to-indigo-100/50 hover:from-indigo-100 hover:to-indigo-200/50 rounded-xl p-4 border border-indigo-200 transition-all duration-200">
                      <div className="text-xs font-medium text-indigo-600 mb-1.5">HCH</div>
                      <div className="text-2xl font-bold text-indigo-900">{report.subscribed_powers.hch}</div>
                      <div className="text-xs text-indigo-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hph && (
                    <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100/50 hover:from-purple-100 hover:to-purple-200/50 rounded-xl p-4 border border-purple-200 transition-all duration-200">
                      <div className="text-xs font-medium text-purple-600 mb-1.5">HPH</div>
                      <div className="text-2xl font-bold text-purple-900">{report.subscribed_powers.hph}</div>
                      <div className="text-xs text-purple-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hpb && (
                    <div className="group relative bg-gradient-to-br from-pink-50 to-pink-100/50 hover:from-pink-100 hover:to-pink-200/50 rounded-xl p-4 border border-pink-200 transition-all duration-200">
                      <div className="text-xs font-medium text-pink-600 mb-1.5">HPB</div>
                      <div className="text-2xl font-bold text-pink-900">{report.subscribed_powers.hpb}</div>
                      <div className="text-xs text-pink-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hce && (
                    <div className="group relative bg-gradient-to-br from-teal-50 to-teal-100/50 hover:from-teal-100 hover:to-teal-200/50 rounded-xl p-4 border border-teal-200 transition-all duration-200">
                      <div className="text-xs font-medium text-teal-600 mb-1.5">HCE</div>
                      <div className="text-2xl font-bold text-teal-900">{report.subscribed_powers.hce}</div>
                      <div className="text-xs text-teal-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.hpe && (
                    <div className="group relative bg-gradient-to-br from-emerald-50 to-emerald-100/50 hover:from-emerald-100 hover:to-emerald-200/50 rounded-xl p-4 border border-emerald-200 transition-all duration-200">
                      <div className="text-xs font-medium text-emerald-600 mb-1.5">HPE</div>
                      <div className="text-2xl font-bold text-emerald-900">{report.subscribed_powers.hpe}</div>
                      <div className="text-xs text-emerald-400 mt-0.5">kW</div>
                    </div>
                  )}
                  {report.subscribed_powers.pointe && (
                    <div className="group relative bg-gradient-to-br from-red-50 to-red-100/50 hover:from-red-100 hover:to-red-200/50 rounded-xl p-4 border border-red-200 transition-all duration-200">
                      <div className="text-xs font-medium text-red-600 mb-1.5">Pointe</div>
                      <div className="text-2xl font-bold text-red-900">{report.subscribed_powers.pointe}</div>
                      <div className="text-xs text-red-400 mt-0.5">kW</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 px-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 mb-3">
                    <Zap className="h-6 w-6 text-amber-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    Aucune puissance souscrite
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Les puissances n'ont pas été extraites du PDF
                  </p>
                  {!isPublicView && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEditModal(true)}
                      className="hover:bg-amber-50"
                    >
                      <Edit size={16} className="mr-2" />
                      Saisir manuellement
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="monthly" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="monthly">Mensuel</TabsTrigger>
            <TabsTrigger value="hourly">Horaire</TabsTrigger>
            <TabsTrigger value="comparison">Écarts</TabsTrigger>
            <TabsTrigger value="analysis">Analyse</TabsTrigger>
          </TabsList>

          {/* Monthly Tab */}
          <TabsContent value="monthly" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Évolution mensuelle de consommation + projection fin d'année</CardTitle>
                <CardDescription>Comparaison entre cette année et l'année dernière</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis label={{ value: 'MWh', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="estimated"
                        fill="#F9C80E"
                        name="Consommation finale - Estimé"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="measured"
                        fill="#397DFF"
                        name="Consommation finale - Mesuré"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="previousYear"
                        fill="#687175"
                        name="Consommation finale mesurée (N-1)"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hourly Tab */}
          <TabsContent value="hourly" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Courbe de charge horaire</CardTitle>
                    <CardDescription>
                      {report.hourly_data && report.hourly_data.length > 0 
                        ? (
                          <>
                            {report.hourly_data.length} points de données disponibles
                            {report.hourly_data_aggregated === 'daily' && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (agrégées par jour - pic max)
                              </span>
                            )}
                            {(() => {
                              const subscribedPowers = report.subscribed_powers || {};
                              const maxSubscribed = Math.max(
                                subscribedPowers.base || 0,
                                subscribedPowers.hcb || 0,
                                subscribedPowers.hch || 0,
                                subscribedPowers.hpb || 0,
                                subscribedPowers.hph || 0
                              );
                              if (maxSubscribed > 0) {
                                return null; // Legend removed - info is on the chart
                              }
                            })()}
                          </>
                        )
                        : "Aucune donnée horaire disponible pour ce rapport"
                      }
                    </CardDescription>
                  </div>
                  {!isPublicView && (
                    <div>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleHourlyCsvUpload}
                        className="hidden"
                        id="hourly-csv-upload"
                        disabled={uploadingCsv}
                      />
                      <label htmlFor="hourly-csv-upload">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={uploadingCsv}
                          asChild
                        >
                          <span>
                            {uploadingCsv ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Import en cours...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Importer CSV
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {report.hourly_data && report.hourly_data.length > 0 ? (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={report.hourly_data}
                        margin={{ top: 5, right: 20, left: 10, bottom: 40 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="hour" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval="preserveStartEnd"
                          tick={{ fontSize: 11 }}
                          stroke="#6b7280"
                        />
                        <YAxis 
                          label={{ value: 'kW', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                          tick={{ fontSize: 12 }}
                          stroke="#6b7280"
                          domain={[0, (dataMax) => {
                            // Inclure la puissance souscrite dans le domaine si elle est définie
                            const subscribedPowers = report.subscribed_powers || {};
                            let maxSubscribedPower = Math.max(
                              subscribedPowers.base || 0,
                              subscribedPowers.hcb || 0,
                              subscribedPowers.hch || 0,
                              subscribedPowers.hpb || 0,
                              subscribedPowers.hph || 0,
                              subscribedPowers.hc || 0,
                              subscribedPowers.hp || 0,
                              subscribedPowers.pointe || 0,
                              report.subscribed_power_kw || 0
                            );
                            // Ajouter 10% de marge au-dessus
                            const maxValue = Math.max(dataMax, maxSubscribedPower);
                            return Math.ceil(maxValue * 1.1);
                          }]}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px'
                          }}
                          labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                        />
                        
                        {/* Ligne de référence pour la puissance souscrite */}
                        {(() => {
                          // Calculer la puissance souscrite max à afficher
                          const subscribedPowers = report.subscribed_powers || {};
                          let maxSubscribedPower = Math.max(
                            subscribedPowers.base || 0,
                            subscribedPowers.hcb || 0,
                            subscribedPowers.hch || 0,
                            subscribedPowers.hpb || 0,
                            subscribedPowers.hph || 0,
                            subscribedPowers.hc || 0,
                            subscribedPowers.hp || 0,
                            subscribedPowers.pointe || 0
                          );
                          
                          // Fallback: utiliser subscribed_power_kw si aucune puissance détaillée
                          if (maxSubscribedPower === 0 && report.subscribed_power_kw) {
                            maxSubscribedPower = report.subscribed_power_kw;
                          }
                          
                          if (maxSubscribedPower > 0) {
                            return (
                              <ReferenceLine 
                                y={maxSubscribedPower} 
                                stroke="#ef4444" 
                                strokeWidth={2}
                                strokeDasharray="8 4"
                                label={{ 
                                  value: `Puissance souscrite: ${maxSubscribedPower} kW`, 
                                  position: 'insideTopRight',
                                  fill: '#ef4444',
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                  offset: 10
                                }}
                              />
                            );
                          }
                          return null;
                        })()}
                        
                        <Line
                          type="monotone"
                          dataKey="power"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={false}
                          name="Puissance atteinte (kW)"
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    {/* Légende combinée */}
                    <div className="flex items-center justify-center gap-6 mt-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-0.5 bg-primary rounded"></div>
                        <span className="text-muted-foreground">Puissance atteinte (kW)</span>
                      </div>
                      {(report.subscribed_power_kw || (report.subscribed_powers && Object.values(report.subscribed_powers).some(v => v > 0))) && (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-0" style={{ borderTop: '2px dashed #ef4444' }}></div>
                          <span className="text-red-500">Puissance souscrite</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-accent rounded-full flex items-center justify-center">
                      <Zap className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Les données horaires ne sont pas disponibles dans le PDF.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Vous pouvez fournir un fichier CSV avec les colonnes : <code>hour</code>, <code>power</code>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertDescription className="text-sm">
                <strong>Note :</strong> Les données de comparaison (2024) peuvent correspondre à des mois différents selon la période couverte par le rapport PDF d'origine. 
                Les pourcentages reflètent l'évolution entre les deux périodes disponibles.
              </AlertDescription>
            </Alert>
            
            <Card>
              <CardHeader>
                <CardTitle>Tableau comparatif mensuel</CardTitle>
                <CardDescription>Détails mois par mois avec écarts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Mois (Année N)</th>
                        <th className="text-right py-3 px-4">Consommation N</th>
                        <th className="text-right py-3 px-4">Consommation N-1</th>
                        <th className="text-right py-3 px-4">Écart</th>
                        <th className="text-center py-3 px-4">Variation %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map((row, index) => {
                        // Use measured + estimated as current year total
                        const currentYearTotal = (row.measured || 0) + (row.estimated || 0);
                        
                        // Round values to 1 decimal for consistent display AND calculation
                        const currentRounded = Math.round(currentYearTotal * 10) / 10;
                        const previousRounded = Math.round((row.previousYear || 0) * 10) / 10;
                        
                        const diff = currentRounded - previousRounded;
                        const diffPercent = previousRounded > 0 
                          ? ((diff / previousRounded) * 100).toFixed(1) 
                          : 0;
                        
                        return (
                          <tr key={index} className="border-b hover:bg-accent/50">
                            <td className="py-3 px-4 font-medium">{row.month} 2025</td>
                            <td className="text-right py-3 px-4">
                              {currentRounded.toFixed(1)} MWh
                              {row.estimated > 0 && <span className="text-xs text-pink-500 ml-1">(estimé)</span>}
                            </td>
                            <td className="text-right py-3 px-4">{previousRounded.toFixed(1)} MWh</td>
                            <td className={`text-right py-3 px-4 font-semibold ${diff < 0 ? 'text-success' : diff > 0 ? 'text-destructive' : ''}`}>
                              {diff > 0 ? '+' : ''}{diff.toFixed(1)} MWh
                            </td>
                            <td className="text-center py-3 px-4">
                              {Math.abs(diffPercent) < 5 ? (
                                <Badge variant="secondary">
                                  <Minus className="w-3 h-3 mr-1" />
                                  ± {Math.abs(diffPercent)}%
                                </Badge>
                              ) : diff < 0 ? (
                                <Badge variant="outline" className="bg-success/10 text-success border-success">
                                  <ArrowDown className="w-3 h-3 mr-1" />
                                  {Math.abs(diffPercent)}%
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">
                                  <ArrowUp className="w-3 h-3 mr-1" />
                                  {Math.abs(diffPercent)}%
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Analyse Energy Manager
                    </CardTitle>
                    <CardDescription>Analyse approfondie de vos consommations énergétiques</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isPublicView && !isEditingAnalysis && (
                      <Button 
                        onClick={generateAiAnalysis}
                        disabled={loadingAiAnalysis}
                        size="sm"
                      >
                        {loadingAiAnalysis ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Analyse en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-4 w-4" />
                            {aiAnalysis ? 'Régénérer l\'analyse' : 'Analyser avec Energy Manager'}
                          </>
                        )}
                      </Button>
                    )}
                    {aiAnalysis && !isPublicView && !isEditingAnalysis && !loadingAiAnalysis && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={startEditingAnalysis}
                        className="border-[#397DFF]/30 text-[#397DFF] hover:bg-[#397DFF]/10"
                        data-testid="edit-analysis-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      <div className="flex flex-col gap-2">
                        <p className="font-semibold">❌ Erreur</p>
                        <p>{aiError}</p>
                        <p className="text-xs">Veuillez réessayer. Si le problème persiste, contactez le support.</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {!aiAnalysis && !loadingAiAnalysis && !aiError && (
                  <div className="text-center py-12">
                    <Sparkles className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Analyse Energy Manager disponible</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Obtenez une analyse détaillée de vos consommations avec des recommandations personnalisées
                    </p>
                    {!isPublicView && (
                      <Button onClick={generateAiAnalysis}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Lancer l'analyse
                      </Button>
                    )}
                  </div>
                )}

                {loadingAiAnalysis && (
                  <div className="text-center py-12">
                    <Loader2 className="mx-auto h-12 w-12 text-primary animate-spin mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Analyse en cours...</h3>
                    <p className="text-sm text-muted-foreground">
                      Energy Manager analyse vos données de consommation. Cela peut prendre quelques secondes.
                    </p>
                  </div>
                )}

                {aiAnalysis && !loadingAiAnalysis && !isEditingAnalysis && (
                  <AiAnalysisDisplay analysis={aiAnalysis} />
                )}

                {/* Edit Mode */}
                {isEditingAnalysis && (
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-inner border border-[#397DFF]/30">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-5 h-5 text-[#397DFF]" />
                        <h3 className="font-semibold text-[#191D1E] dark:text-white">Mode édition</h3>
                      </div>
                      <p className="text-xs text-[#939BA0]">
                        Utilisez le format Markdown : ## Titre, ### Section, **gras**, - liste
                      </p>
                    </div>
                    <textarea
                      value={editedAnalysis}
                      onChange={(e) => setEditedAnalysis(e.target.value)}
                      className="w-full h-96 p-4 border border-[#BFC6CC]/50 rounded-lg text-sm font-mono
                        bg-[#F5F6F9] dark:bg-slate-900 text-[#3C4649] dark:text-gray-300
                        focus:outline-none focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF]
                        resize-y"
                      placeholder="Modifiez l'analyse ici..."
                      data-testid="analysis-textarea"
                    />
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-xs text-[#939BA0]">
                        {editedAnalysis.length} caractères
                      </p>
                      <div className="flex items-center gap-3">
                        <Button 
                          variant="outline"
                          onClick={cancelEditingAnalysis}
                          disabled={savingAnalysis}
                          className="border-[#BFC6CC] text-[#687175]"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                        <Button 
                          onClick={saveEditedAnalysis}
                          disabled={savingAnalysis || !editedAnalysis.trim()}
                          className="bg-[#10D183] hover:bg-[#0eb874] text-white"
                          data-testid="save-analysis-btn"
                        >
                          {savingAnalysis ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sauvegarde...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Sauvegarder
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Modal */}
      {showEditModal && report && (
        <EditConsumptionDataModal
          report={report}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedReport) => {
            setReport(updatedReport);
            toast.success('Données mises à jour avec succès');
          }}
          getAuthHeader={getAuthHeader}
        />
      )}

      {/* Contact Info Modal */}
      <ContactModal
        open={showContactModal}
        onOpenChange={setShowContactModal}
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        onSave={handleSaveContactInfo}
        saving={savingContact}
      />
    </div>
  );
};

export default ConsumptionReportViewPage;
